import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/api/supabase';

// ── VAPID public key (generated for this project) ───────────────────────────
// The private key lives in the Supabase Edge Function secret: VAPID_PRIVATE_KEY
const VAPID_PUBLIC_KEY = 'BJBT4Awu2rQm3QxbvYOLcEhXgKsvbo1fCR30UFcLJsLukPbkPw0fSp30_N1Rq-2oBA9CyM0CPiMZs1wWOacMvPE';

// Convert base64 VAPID key to Uint8Array (required by pushManager.subscribe)
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

/**
 * usePushNotifications — manages the full push subscription lifecycle.
 *
 * Usage:
 *   const { permission, isSubscribed, subscribe, unsubscribe, isSupported } = usePushNotifications(userId);
 */
export function usePushNotifications(userId) {
  const [permission,    setPermission   ] = useState(Notification?.permission ?? 'default');
  const [isSubscribed,  setIsSubscribed  ] = useState(false);
  const [isLoading,     setIsLoading     ] = useState(false);
  const [error,         setError         ] = useState(null);
  const swRegRef = useRef(null);

  const isSupported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

  // ── Register service worker once ───────────────────────────────────────
  useEffect(() => {
    if (!isSupported) return;

    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((reg) => {
        swRegRef.current = reg;
        console.log('[SW] Registered:', reg.scope);

        // Listen for service worker updates
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          newWorker?.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
      })
      .catch((err) => {
        console.error('[SW] Registration failed:', err);
        setError('Service Worker registration failed. Please refresh the page.');
      });
  }, [isSupported]);

  // ── Check existing subscription on mount ───────────────────────────────
  useEffect(() => {
    if (!isSupported || !userId) return;

    const checkSubscription = async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        swRegRef.current = reg;
        const existing = await reg.pushManager.getSubscription();
        setIsSubscribed(!!existing);
        setPermission(Notification.permission);
      } catch (err) {
        console.warn('[Push] Could not check subscription:', err);
      }
    };

    checkSubscription();
  }, [isSupported, userId]);

  // ── Subscribe ───────────────────────────────────────────────────────────
  const subscribe = useCallback(async () => {
    if (!isSupported || !userId) {
      setError('Push notifications are not supported on this browser or you are not logged in.');
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      console.log('[Push] Requesting permission...');
      // 1. Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      
      if (perm !== 'granted') {
        console.warn('[Push] Permission denied:', perm);
        setError('Notification permission denied. Please click the lock icon in the address bar and allow notifications.');
        setIsLoading(false);
        return;
      }

      // 2. Get SW registration
      const reg = swRegRef.current || (await navigator.serviceWorker.ready);
      swRegRef.current = reg;

      // 3. Create push subscription
      console.log('[Push] Creating subscription...');
      let subscription = await reg.pushManager.getSubscription();
      
      // If subscription exists but we are here, it might be stale. Re-subscribe.
      if (subscription) {
        await subscription.unsubscribe();
      }
      
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      // 4. Save subscription to Supabase
      console.log('[Push] Saving to database...');
      const subJson = subscription.toJSON();
      const { error: dbError } = await supabase
        .from('push_subscriptions')
        .upsert(
          {
            user_id:  userId,
            endpoint: subJson.endpoint,
            p256dh:   subJson.keys?.p256dh,
            auth:     subJson.keys?.auth,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' }
        );

      if (dbError) throw dbError;

      setIsSubscribed(true);
      console.log('[Push] Subscribed successfully');
    } catch (err) {
      console.error('[Push] Subscribe error:', err);
      setError(err.message || 'Failed to enable notifications. Try clearing browser cache or check permissions.');
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, userId]);

  // ── Unsubscribe ─────────────────────────────────────────────────────────
  const unsubscribe = useCallback(async () => {
    if (!isSupported || !userId) return;
    setIsLoading(true);
    setError(null);

    try {
      const reg = swRegRef.current || (await navigator.serviceWorker.ready);
      const subscription = await reg.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }

      // Remove from Supabase
      await supabase.from('push_subscriptions').delete().eq('user_id', userId);

      setIsSubscribed(false);
      console.log('[Push] Unsubscribed');
    } catch (err) {
      console.error('[Push] Unsubscribe error:', err);
      setError(err.message || 'Failed to disable notifications');
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, userId]);

  // ── Send a test notification (for debugging) ────────────────────────────
  const sendTestNotification = useCallback(async () => {
    if (!isSupported) return;
    if (Notification.permission !== 'granted') {
      setError('Please enable notifications first.');
      return;
    }
    
    try {
      const reg = swRegRef.current || (await navigator.serviceWorker.ready);
      // Using a unique tag and renotify: true ensures the browser alerts the user every time
      await reg.showNotification('🌸 Lilac Valley CRM', {
        body: `Test notification sent at ${new Date().toLocaleTimeString()}`,
        icon: '/logo192.png',
        badge: '/favicon.ico',
        tag: `test-${Date.now()}`,
        renotify: true,
        vibrate: [200, 100, 200],
      });
      console.log('[Push] Test notification sent');
    } catch (err) {
      console.error('[Push] Test notification error:', err);
      setError('Failed to send test notification.');
    }
  }, [isSupported]);

  return {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    sendTestNotification,
    clearError: () => setError(null)
  };
}

